import { useState, useRef } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { C } from "./data";

const LINE = "#E3E0D8";
const card = { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14, padding: 14, marginBottom: 12 };
const label = { fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 5, display: "block" };
const input = { width: "100%", padding: "11px 12px", border: `1px solid ${LINE}`, borderRadius: 10, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", background: "#fff", color: C.ink };
const cta = (busy) => ({ width: "100%", padding: 14, background: C.red, color: "#fff", border: 0, borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 });

const Row = ({ children }) => <div style={{ display: "flex", gap: 10 }}>{children}</div>;
const Field = ({ l, v, on, t, ph }) => (
  <div style={{ flex: 1, marginBottom: 11 }}>
    <span style={label}>{l}</span>
    <input value={v || ""} placeholder={ph || ""} onChange={(e) => on(e.target.value)} data-testid={t} style={input} />
  </div>
);

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (ref.current.width / r.width), y: (t.clientY - r.top) * (ref.current.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = C.ink; ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={520} height={150} data-testid="do-signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ width: "100%", height: 120, background: "#fff", border: `1px dashed ${LINE}`, borderRadius: 10, touchAction: "none" }} />
      <button type="button" onClick={clear} data-testid="do-signature-clear"
        style={{ marginTop: 6, background: "none", border: 0, color: C.slate, fontSize: 12, textDecoration: "underline", cursor: "pointer" }}>Clear signature</button>
    </div>
  );
}

// Presentational debit-order authority form. onSubmit(formValues) must return a promise.
export default function DebitOrderForm({ initial = {}, plans = [], submitLabel = "Sign debit order authority", onSubmit }) {
  const [f, setF] = useState({
    name: initial.name || "", surname: initial.surname || "", id_number: initial.id_number || "",
    mobile: initial.mobile || "", email: initial.email || "", home_address: "",
    account_holder: initial.account_holder || "", bank: "", branch_name: "", branch_code: "",
    account_number: "", account_type: "Cheque/Current", signature: null,
    plan_code: plans.some((p) => p.code === initial.plan_code) ? initial.plan_code : "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const chosen = plans.find((p) => p.code === f.plan_code);

  const submit = async () => {
    if (plans.length && !chosen) return toast.error("Please choose your plan");
    for (const [k, nm] of [["name", "First name"], ["surname", "Surname"], ["id_number", "ID number"], ["account_holder", "Account holder"], ["bank", "Bank"], ["branch_code", "Branch code"], ["account_number", "Account number"]]) {
      if (!String(f[k] || "").trim()) return toast.error(`${nm} is required`);
    }
    if (String(f.id_number).replace(/\D/g, "").length !== 13) return toast.error("Enter your 13-digit South African ID number");
    if (!f.signature) return toast.error("Please sign the debit-order authority");
    setBusy(true);
    try { await onSubmit(f); }
    catch (e) { toast.error("Couldn't submit", { description: e.response?.data?.detail || "Please try again." }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="debit-order-form">
      {plans.length > 0 && (
        <div style={card} data-testid="do-plan-picker">
          <span style={label}>Your plan</span>
          <div style={{ display: "flex", gap: 8 }}>
            {plans.map((p) => {
              const on = f.plan_code === p.code;
              return (
                <button key={p.code} type="button" onClick={() => set("plan_code", p.code)} data-testid={`do-plan-${p.code}`}
                  style={{ flex: 1, padding: "11px 6px", borderRadius: 10, cursor: "pointer", textAlign: "center", fontFamily: "inherit",
                    border: `1.5px solid ${on ? C.red : LINE}`, background: on ? `${C.red}0d` : "#fff", transition: "border-color 0.15s ease, background-color 0.15s ease" }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>{p.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: on ? C.red : C.ink, marginTop: 2 }}>R{p.price}<span style={{ fontSize: 11, fontWeight: 600, color: C.slate }}>pm</span></div>
                  <div style={{ fontSize: 10.5, color: C.slate, marginTop: 2 }}>{p.blurb}</div>
                </button>
              );
            })}
          </div>
          {chosen && <div data-testid="do-plan-summary" style={{ fontSize: 12.5, color: C.ink, marginTop: 9 }}>{chosen.label} · <b>R{chosen.price} per month</b>, VAT included, debited on the 1st.</div>}
        </div>
      )}
      <div style={card}>
        <Row><Field l="First name" v={f.name} on={(v) => set("name", v)} t="do-name" /><Field l="Surname" v={f.surname} on={(v) => set("surname", v)} t="do-surname" /></Row>
        <Row><Field l="ID number" v={f.id_number} on={(v) => set("id_number", v)} t="do-id" ph="13 digits" /><Field l="Cell number" v={f.mobile} on={(v) => set("mobile", v)} t="do-cell" /></Row>
        <Field l="Home address" v={f.home_address} on={(v) => set("home_address", v)} t="do-address" />
      </div>
      <div style={card}>
        <Field l="Account holder" v={f.account_holder} on={(v) => set("account_holder", v)} t="do-holder" />
        <Row><Field l="Bank" v={f.bank} on={(v) => set("bank", v)} t="do-bank" /><Field l="Branch code" v={f.branch_code} on={(v) => set("branch_code", v)} t="do-branch-code" /></Row>
        <Row>
          <Field l="Account number" v={f.account_number} on={(v) => set("account_number", v)} t="do-account" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={label}>Account type</span>
            <select value={f.account_type} onChange={(e) => set("account_type", e.target.value)} data-testid="do-account-type" style={input}>
              <option>Cheque/Current</option><option>Savings</option><option>Transmission</option>
            </select>
          </div>
        </Row>
      </div>
      <div style={card}>
        <span style={label}>Signature</span>
        <SignaturePad onChange={(v) => set("signature", v)} />
        <p style={{ fontSize: 11.5, color: C.slate, lineHeight: 1.6, marginTop: 8 }}>
          By signing you authorise Acuideas (Pty) Ltd to debit the account above with {chosen ? `R${chosen.price} per month for the ResQ1 ${chosen.label} plan` : "the agreed ResQ1 subscription fees"}. Your cover continues without interruption. To cancel, contact ResQ1.
        </p>
      </div>
      <button onClick={submit} disabled={busy} data-testid="do-submit" style={cta(busy)}>
        {busy ? <><Loader2 size={16} style={{ animation: "rqspin 0.9s linear infinite" }} /> Submitting…</> : submitLabel}
      </button>
      <style>{"@keyframes rqspin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}
